import * as React from 'react';

import { Form } from 'antd';
import { Notification } from 'farmhub-ui-core';
import { useHistory } from 'react-router-dom';

import { GoogleApi } from 'api';
import { useAppDispatch, useAppSelector } from 'app';
import SelectCategoriesStep from 'containers/App/Auth/components/SelectCategoriesStep';
import SelectCertificatesStep from 'containers/App/Auth/components/SelectCertificateStep';
import {
  createAccount,
  reloadAccounts,
  selectAppDevice,
  updateUserQuantity,
} from 'slice';
import { DEVICES } from 'utils';
import { formatPhoneNumber } from 'utils/function';

import ContentDesktop from './ContentDesktop';
import ContentMobile from './ContentMobile';
import InformationStep from './InformationStep';
import { Content } from './styles';

interface FarmCreatePageContentProps {
  setVisibleModal?: (e: boolean) => void;
  setCurrentStep?: (e: number) => void;
}

export const FarmCreatePageContent = ({
  setVisibleModal,
  setCurrentStep,
}: FarmCreatePageContentProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [form] = Form.useForm();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);
  const [bodySaved, setBodySaved] = React.useState<any>({});
  const [isValidEmail, setIsValidEmail] = React.useState(true);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = React.useState(true);
  const appDevice = useAppSelector(selectAppDevice);

  const [selectedItem, setSelectedItem] = React.useState({
    certificationIDs: [],
    categoryIDs: [],
  });
  const [hasErrorCode, setHasErrorCode] = React.useState<boolean>(false);

  const handleChangeCurrentStep = (e: number) => {
    setCurrentTab(e);
  };

  const handleSubmitStep = async (current?: number) => {
    if (currentTab === 2 && current === 1) {
      setCurrentTab(current);
    }
    form.validateFields().then(async values => {
      if (currentTab === 0) {
        if (!isValidEmail || !isValidPhoneNumber) {
          return;
        }
        const body = {
          ...values,
          type: 1,
          displayName: values.fullName,
          phoneNumber: formatPhoneNumber(values.phoneNumber),
        };

        setBodySaved({
          ...bodySaved,
          ...body,
        });
        setCurrentTab(currentTab + 1);
      }

      if (currentTab === 1) {
        const location = form.getFieldsValue([
          'provinceCode',
          'districtCode',
          'wardCode',
          'latitude',
          'longitude',
          'address',
        ]);

        if (
          !location.provinceCode ||
          !location.districtCode ||
          !location.wardCode
        ) {
          setHasErrorCode(true);
          return;
        }

        if (
          location?.provinceCode &&
          location?.districtCode &&
          location?.wardCode &&
          !location?.latitude &&
          !location?.longitude
        ) {
          const result = await GoogleApi.getCenterPointWard({
            provinceCode: location?.provinceCode,
            districtCode: location?.districtCode,
            wardCode: location?.wardCode,
          });
          location.latitude = result.data.latitude;
          location.longitude = result.data.longitude;
        }

        const body = {
          ...values,
          ...location,
        };

        setBodySaved({
          ...bodySaved,
          ...body,
        });
        setCurrentTab(currentTab + 1);
      }
    });
  };

  const handleFinalStep = async () => {
    const { certificationIDs, categoryIDs } = selectedItem;
    if (categoryIDs.length === 0) {
      Notification('warning', 'Vui lòng chọn ít nhất một danh mục sản phẩm');
      return;
    }
    const body = {
      ...bodySaved,
      certificationIDs,
      categoryIDs,
    };

    Object.keys(body).forEach(e => {
      if (body[e] === undefined) {
        delete body[e];
      }
    });

    try {
      setIsSubmitting(true);
      await dispatch(createAccount(body)).unwrap();
      await dispatch(
        updateUserQuantity({ count: 1, type: 'totalFarmAccount' })
      );
      await dispatch(reloadAccounts());

      if (!setVisibleModal) {
        history.push(`/account/list`);
      } else {
        setIsSubmitting(false);
        setCurrentStep && setCurrentStep(0);
      }
    } catch (error: any) {
      setIsSubmitting(false);
      Notification('error', 'Tạo nông trại thất bại', error);
    }
  };

  const handleNextStep = () => {
    switch (currentTab) {
      case 0:
      case 1:
        return handleSubmitStep();
      case 2:
        return handleFinalStep();
      default:
        return 0;
    }
  };

  const ListStep = [
    <InformationStep
      form={form}
      hasErrorCode={hasErrorCode}
      isValidEmail={isValidEmail}
      isValidPhoneNumber={isValidPhoneNumber}
      setHasErrorCode={setHasErrorCode}
      setIsValidEmail={setIsValidEmail}
      setIsValidPhoneNumber={setIsValidPhoneNumber}
    />,
    <Content device={appDevice.device}>
      <SelectCategoriesStep data={selectedItem} setData={setSelectedItem} />
    </Content>,
    <Content device={appDevice.device}>
      <SelectCertificatesStep data={selectedItem} setData={setSelectedItem} />
    </Content>,
  ];

  const renderElements = {
    [DEVICES.DESKTOP]: (
      <ContentDesktop
        ListStep={ListStep}
        currentTab={currentTab}
        handleChangeCurrentStep={handleChangeCurrentStep}
        handleNextStep={handleNextStep}
        handleSubmitStep={handleSubmitStep}
        isSubmitting={isSubmitting}
      />
    ),
    [DEVICES.MOBILE]: (
      <ContentMobile
        ListStep={ListStep}
        currentTab={currentTab}
        handleChangeCurrentStep={handleChangeCurrentStep}
        handleNextStep={handleNextStep}
        handleSubmitStep={handleSubmitStep}
        isSubmitting={isSubmitting}
      />
    ),
    [DEVICES.TABLET]: (
      <ContentMobile
        ListStep={ListStep}
        currentTab={currentTab}
        handleChangeCurrentStep={handleChangeCurrentStep}
        handleNextStep={handleNextStep}
        handleSubmitStep={handleSubmitStep}
        isSubmitting={isSubmitting}
      />
    ),
  };

  return <>{renderElements[appDevice.device]}</>;
};
